import React, { useState } from 'react';
import { Phone, Mail, Instagram, ChevronDown } from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';
import Tilt from './Tilt';

// Snapshot once at module load: are we on a touch / older device?
// Used to skip the JS hover-to-open behavior on mobile (iOS Safari
// emulates `mouseenter` on the first touch and never reliably fires
// `mouseleave`, leaving trainer cards visually stuck in the heavy
// "open" state — animated red shadow, scaled image — which compounds
// GPU memory cost on older devices). On mobile users tap the explicit
// "Read bio + contact" button to open the bio instead.
const IS_COARSE = typeof window !== 'undefined' && window.matchMedia
  && window.matchMedia('(pointer: coarse)').matches;

export default function Trainers() {
  const { content } = useSiteContent();
  const trainers = content.trainers || [];
  const [openId, setOpenId] = useState(null);

  return (
    <section id="trainers" data-testid="trainers-section" className="relative py-16 sm:py-24 lg:py-32 bg-black overflow-hidden">
      {/* Decorative red blur blobs — desktop only. On mobile, two
          `blur-3xl` (64px backdrop blur) layers contribute meaningful
          GPU memory pressure on iOS Safari / older Android with no
          functional value. Hidden under `sm:block`. */}
      <div className="hidden sm:block absolute top-1/4 right-0 w-1/3 h-1/3 bg-red-600/10 blur-3xl rounded-full pointer-events-none" />
      <div className="hidden sm:block absolute bottom-0 left-0 w-1/4 h-1/3 bg-red-600/8 blur-3xl rounded-full pointer-events-none" />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-16 reveal">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-[2px] w-10 bg-red-600" />
            <span className="text-xs tracking-[0.4em] text-red-500 font-semibold">PERSONAL TRAINERS</span>
            <div className="h-[2px] w-10 bg-red-600" />
          </div>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.9] tracking-tight">
            COACHES WHO <span className="text-gradient-red">CARE</span>
          </h2>
          <p className="max-w-2xl mx-auto mt-6 text-zinc-400">
            The journey to your fitness goals can be hard enough — there&rsquo;s no reason to do it alone. Tap any coach to read their bio.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
          {trainers.map((t) => {
            const isOpen = openId === t.id;
            // Hover handlers are desktop-only — see IS_COARSE note above.
            const hoverHandlers = IS_COARSE ? {} : {
              onMouseEnter: () => setOpenId(t.id),
              onMouseLeave: () => setOpenId(null),
            };
            return (
              <Tilt key={t.id} max={isOpen ? 0 : 7} scale={1.012} className="trainer-card-tilt">
              <article
                data-testid={`trainer-card-${t.id}`}
                {...hoverHandlers}
                className={`trainer-card group relative rounded-2xl overflow-hidden border bg-zinc-950 transition-all duration-500 ${
                  isOpen ? 'border-red-500 sm:-translate-y-1 sm:shadow-[0_30px_60px_-20px_rgba(239,43,43,0.45)]' : 'border-zinc-900'
                } ${t.featured ? 'trainer-featured' : ''}`}
                data-cursor="hover"
              >
                {/* === Image (clean photos — full subject including head visible) === */}
                <div className="relative aspect-[3/4] w-full overflow-hidden bg-gradient-to-br from-zinc-900 via-zinc-950 to-black">
                  <img
                    src={t.image}
                    alt={t.name}
                    /* EAGER on the trainer grid: this is the section users
                       jump to most often (linked from nav + Hero CTA) and
                       lazy-loading 4 portraits while the section is below
                       the fold made trainer images stay blank for 30 s+
                       on slower mobile/iPad Safari. Each WebP is ~50 KB —
                       eagerly decoding all 4 is cheap and removes the
                       blank-card flash. */
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    width="600"
                    height="800"
                    style={{ objectPosition: 'center 18%' }}
                    onError={(e) => {
                      // Hide the failed image so the dark gradient background
                      // + initials block below stay visible — never show iOS
                      // broken-image icon.
                      e.currentTarget.style.display = 'none';
                      const fb = e.currentTarget.nextElementSibling;
                      if (fb && fb.classList.contains('trainer-img-fallback')) {
                        fb.style.opacity = '1';
                      }
                    }}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                  {/* Graceful fallback — shows the trainer's initials inside
                      a styled red badge if the image ever fails to load.
                      Hidden by default (opacity 0); revealed by onError. */}
                  <div
                    aria-hidden
                    className="trainer-img-fallback absolute inset-0 flex flex-col items-center justify-center opacity-0 transition-opacity duration-300 pointer-events-none"
                  >
                    <div className="font-display text-5xl text-red-500/80 tracking-widest">
                      {(t.name || '').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="text-[10px] tracking-[0.3em] text-zinc-500 mt-2 font-bold">
                      {(t.role || '').toUpperCase()}
                    </div>
                  </div>
                  <div className={`absolute inset-0 ring-1 ring-inset transition-colors duration-500 pointer-events-none ${isOpen ? 'ring-red-500/40' : 'ring-transparent'}`} />
                </div>

                {/* === Body: name plate + summary + dropdown === */}
                <div className="p-5">
                  <div className="text-[10px] tracking-[0.3em] text-red-500 font-bold uppercase">
                    {t.role}
                  </div>
                  <h3
                    data-testid={`trainer-name-${t.id}`}
                    className="font-display text-2xl md:text-3xl tracking-tight text-white mt-1.5 leading-none"
                  >
                    {t.name}
                  </h3>
                  <div className="mt-3 h-[2px] w-10 bg-red-600" />

                  <p className="mt-4 text-sm text-zinc-300 leading-relaxed">{t.summary}</p>

                  <button
                    type="button"
                    onClick={() => setOpenId(isOpen ? null : t.id)}
                    aria-expanded={isOpen}
                    aria-controls={`trainer-bio-${t.id}`}
                    data-testid={`trainer-toggle-${t.id}`}
                    data-cursor="hover"
                    className="mt-4 w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-red-600/35 hover:border-red-500 bg-red-600/5 hover:bg-red-600/10 transition-colors"
                  >
                    <span className="text-[10px] tracking-[0.3em] text-red-400 font-bold uppercase">
                      {isOpen ? 'Hide bio' : 'Read bio + contact'}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-red-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {/* Expandable region */}
                  <div
                    id={`trainer-bio-${t.id}`}
                    data-testid={`trainer-bio-${t.id}`}
                    className={`grid transition-all duration-500 ease-out ${
                      isOpen ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0 mt-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="text-xs text-zinc-400 leading-relaxed">{t.bio}</p>

                      <div className="mt-4 space-y-2.5 border-t border-white/5 pt-4">
                        <a
                          href={`tel:${t.phone}`}
                          className="flex items-center gap-2.5 text-xs text-zinc-300 hover:text-red-400 transition"
                          data-cursor="hover"
                        >
                          <Phone className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                          <span className="tabular">{t.phone}</span>
                        </a>
                        <a
                          href={`mailto:${t.email}`}
                          className="flex items-center gap-2.5 text-xs text-zinc-300 hover:text-red-400 transition break-all"
                          data-cursor="hover"
                        >
                          <Mail className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                          <span>{t.email}</span>
                        </a>
                        {t.instagram && (
                          <a
                            href={`https://instagram.com/${t.instagram.replace(/^@/, '')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-2.5 text-xs text-zinc-300 hover:text-red-400 transition"
                            data-cursor="hover"
                          >
                            <Instagram className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                            <span>@{t.instagram.replace(/^@/, '')}</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </article>
              </Tilt>
            );
          })}
        </div>
      </div>
    </section>
  );
}
